/**
 * 把 precheck HealthIssue 的机器 path（如 "brand_config[0].category"）
 * 解析成中文标签 + 定位信息。数据直接 re-export 飞书 schema 的 FIELD_MAP + TABLE_CN_NAMES。
 */
import { FIELD_MAP, TABLE_CN_NAMES, type TableSlug } from '@astro-genbox/feishu-schema/labels';

export { TABLE_CN_NAMES };
export type { TableSlug };

export interface ParsedPath {
  /** "brand_config" */
  tableSlug: TableSlug | null;
  /** "[0]" 里的 0；没有索引就是 null */
  rowIndex: number | null;
  /** "[brand=slug]" 这种过滤式定位，留原文 */
  rowFilter: string | null;
  /** "category" | "languages" | 或嵌套 "inquiry.fields[0].label" */
  fieldPath: string | null;
  /** FIELD_MAP 里该字段的中文名；没匹配到就返回 fieldPath */
  fieldCn: string;
  /** TABLE_CN_NAMES[tableSlug]；没匹配到就返回 tableSlug 原文 */
  tableCn: string;
}

const TABLE_SLUG_RE = /^([a-z_]+)(?:\[([^\]]+)\])?(?:\.(.+))?$/;

export function parseIssuePath(path: string): ParsedPath {
  const m = TABLE_SLUG_RE.exec(path);
  if (!m) {
    return {
      tableSlug: null,
      rowIndex: null,
      rowFilter: null,
      fieldPath: null,
      fieldCn: path,
      tableCn: path,
    };
  }
  const tableSlug = m[1] as TableSlug | undefined;
  const rowSpec = m[2] ?? null;
  const fieldPath = m[3] ?? null;

  const isKnownSlug = tableSlug && tableSlug in TABLE_CN_NAMES;
  const resolvedSlug = isKnownSlug ? (tableSlug as TableSlug) : null;

  const rowIndex = rowSpec !== null && /^\d+$/.test(rowSpec) ? Number(rowSpec) : null;
  const rowFilter = rowSpec !== null && rowIndex === null ? rowSpec : null;

  const tableCn = resolvedSlug ? TABLE_CN_NAMES[resolvedSlug] : (tableSlug ?? path);

  let fieldCn = fieldPath ?? '';
  if (resolvedSlug && fieldPath) {
    // 取第一层键（"languages" / "inquiry.fields[0].label" → "inquiry"）
    const firstKey = fieldPath.split(/[.[]/, 1)[0] ?? fieldPath;
    const tableFields = (FIELD_MAP[resolvedSlug] as { fields: Record<string, { cn: string }> })
      .fields;
    const entry = tableFields[firstKey];
    if (entry) {
      // 只替换首段，保留后面的嵌套路径（如 ".fields[0].label"）
      const rest = fieldPath.slice(firstKey.length);
      fieldCn = entry.cn + rest;
    }
  }

  return {
    tableSlug: resolvedSlug,
    rowIndex,
    rowFilter,
    fieldPath,
    fieldCn,
    tableCn,
  };
}

/** 可由 onboarding-llm ExpandedBrandSchema 生成的字段（表.字段） */
const AI_FILLABLE = new Set<string>([
  'brand_config.brand_name',
  'brand_config.slogan',
  'brand_config.brand_story_short',
  'brand_config.USP_1',
  'brand_config.USP_2',
  'brand_config.USP_3',
  'brand_config.USP_4',
  'brand_config.meta_description',
  'brand_config.positioning_anchor',
  'brand_content.body_markdown',
]);

/** 可由 ai-fill-sku 撰写的字段（表.字段）—— 与 brand-fillable 区分入口按钮 */
const AI_FILLABLE_SKU = new Set<string>([
  'sku.product_name_en',
  'sku.description_en',
  'sku.keywords_en',
  'sku.category_tag',
  'sku.detail_long',
  'sku.bullets',
  'sku.meta_title',
  'sku.meta_description',
]);

export function isAiFillable(parsed: ParsedPath): boolean {
  if (!parsed.tableSlug || !parsed.fieldPath) return false;
  const firstKey = parsed.fieldPath.split(/[.[]/, 1)[0] ?? '';
  return AI_FILLABLE.has(`${parsed.tableSlug}.${firstKey}`);
}

/** 是否属于 ai-fill-sku 能补的 SKU 字段（PrecheckPage 用来决定显示 SKU 按钮） */
export function isAiFillableSku(parsed: ParsedPath): boolean {
  if (parsed.tableSlug !== 'sku' || !parsed.fieldPath) return false;
  const firstKey = parsed.fieldPath.split(/[.[]/, 1)[0] ?? '';
  return AI_FILLABLE_SKU.has(`sku.${firstKey}`);
}

/**
 * 把 Zod 英文 msg 常见几种翻译成中文。没匹配到保留原文。
 * Zod v3 默认 msg：
 *   - "Required"
 *   - "Invalid"
 *   - "Invalid enum value. Expected ... received ..."
 *   - "String must contain at least N character(s)"
 *   - "String must contain at most N character(s)"
 *   - "Number must be greater than or equal to N"
 */
export function translateIssueMessage(msg: string): string {
  if (msg === 'Required') return '必填，请填写';
  if (msg === 'Invalid') return '填写有误';
  const minStr = /^String must contain at least (\d+) character/.exec(msg);
  if (minStr) return `至少需要 ${minStr[1]} 个字符`;
  const maxStr = /^String must contain at most (\d+) character/.exec(msg);
  if (maxStr) return `不能超过 ${maxStr[1]} 个字符`;
  const minNum = /^Number must be greater than or equal to (-?\d+)/.exec(msg);
  if (minNum) return `不能小于 ${minNum[1]}`;
  const maxNum = /^Number must be less than or equal to (-?\d+)/.exec(msg);
  if (maxNum) return `不能大于 ${maxNum[1]}`;
  if (msg.startsWith('Invalid enum value')) {
    // Zod 默认 format："Invalid enum value. Expected '项链' | '耳饰', received '戒子'"
    // 把允许列表 + 用户实际填的值都暴露出来——业务人员看到「允许：项链/耳饰」才知道飞书里改成什么。
    const m = /Expected\s+(.+?),\s+received\s+(?:'([^']*)'|"([^"]*)"|(\S+))/.exec(msg);
    if (m) {
      const expected = (m[1] ?? '')
        .replace(/['"]/g, '')
        .split(/\s*\|\s*/)
        .join(' / ');
      const received = m[2] ?? m[3] ?? m[4] ?? '';
      return `当前值「${received}」不在允许范围；允许：${expected}`;
    }
    return '不在可选范围内';
  }
  if (msg.startsWith('Invalid url')) return '网址格式不对';
  if (msg.startsWith('Invalid email')) return '邮箱格式不对';
  if (msg.startsWith('Expected')) return msg.replace(/^Expected/, '期望');
  return msg;
}
